import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    Alert,
    Box,
    IconButton
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { del, get, post, put } from "../../config/axios";
import toast from "react-hot-toast";

const QNA = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState("");
    const [currentAnswer, setCurrentAnswer] = useState("");
    const [currentId, setCurrentId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch all Q&As on component mount
    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const response = await get('/api/dashboard/website/qna');
            setQuestions(response);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch questions");
            toast.error("Failed to fetch questions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, []);

    const handleOpenDialog = () => {
        setCurrentQuestion("");
        setCurrentAnswer("");
        setCurrentId(null);
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
    };

    const handleAddOrEdit = async () => {
        if (!currentQuestion.trim() || !currentAnswer.trim()) {
            toast.error("Both question and answer are required");
            return;
        }

        try {
            setIsSubmitting(true);
            const qnaData = { question: currentQuestion, answer: currentAnswer };

            if (currentId) {
                // Update existing Q&A
                const response = await put(`/api/dashboard/website/qna/${currentId}`, qnaData);
                setQuestions(questions.map(q =>
                    q._id === currentId ? response.data : q
                ));
                toast.success("Question updated successfully");
            } else {
                // Add new Q&A
                const response = await post('/api/dashboard/website/qna', qnaData);
                toast.success("Question added successfully");
            }

            handleCloseDialog();
        } catch (err) {
            toast.error(err.response?.data?.message || "Operation failed");
        } finally {
            setIsSubmitting(false);
            fetchQuestions()
        }
    };

    const handleEdit = (qna) => {
        setCurrentQuestion(qna.question);
        setCurrentAnswer(qna.answer);
        setCurrentId(qna._id);
        setOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this question?")) {
            try {
                await del(`/api/dashboard/website/qna/${id}`);
                setQuestions(questions.filter(q => q._id !== id));
                toast.success("Question deleted successfully");
            } catch (err) {
                toast.error(err.response?.data?.message || "Failed to delete question");
            }
        }
    };

    // DataGrid columns configuration
    const columns = [
        {
            field: 'question',
            headerName: 'Question',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'answer',
            headerName: 'Answer',
            flex: 1,
            minWidth: 300
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <>
                    <IconButton
                        color="primary"
                        onClick={() => handleEdit(params.row)}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => handleDelete(params.row._id)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </>
            ),
        },
    ];

    if (loading) {
        return (
            <Layout>
                <div style={{ padding: "1rem", display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div style={{ padding: "1rem" }}>
                    <Alert severity="error">{error}</Alert>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{ padding: "1rem" }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2 }}>
                    <Typography variant="h5">
                        Manage Q&A
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenDialog}
                    >
                        Add Question
                    </Button>
                </Box>

                {/* DataGrid Table */}
                <Paper sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                        rows={questions}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        getRowId={(row) => row._id}
                        disableSelectionOnClick
                        sx={{
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                            },
                        }}
                        localeText={{
                            noRowsLabel: 'No questions available',
                        }}
                    />
                </Paper>

                {/* Dialog for Adding/Editing QNA */}
                <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                    <DialogTitle>
                        {currentId ? "Edit Question" : "Add Question"}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Question"
                            variant="outlined"
                            margin="dense"
                            value={currentQuestion}
                            onChange={(e) => setCurrentQuestion(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Answer"
                            variant="outlined"
                            margin="dense"
                            value={currentAnswer}
                            onChange={(e) => setCurrentAnswer(e.target.value)}
                            multiline
                            rows={4}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleAddOrEdit}
                            disabled={isSubmitting || !currentQuestion.trim() || !currentAnswer.trim()}
                        >
                            {isSubmitting ? (
                                <>
                                    {currentId ? "Updating.." : "Adding.."}
                                </>
                            ) : (
                                currentId ? "Update" : "Add"
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </Layout>
    );
};

export default QNA;